import axios from "axios"

import { defineStore, storeToRefs } from "pinia"
import { ref, watch, computed } from "vue"

import { useVersionStore } from "@/stores/version"
import { useRootStore } from "@/stores/root"
import { Capacitor } from "@capacitor/core"
import { Device } from "@capacitor/device"
import { App } from "@capacitor/app"
import { PushNotifications } from "@capacitor/push-notifications"
import { useCommandStore } from "@/stores/command"
import { useRouter } from "vue-router"

export const useApiStore = defineStore(
	"api",
	() => {
		const { appVersion } = storeToRefs(useVersionStore())
		const router = useRouter()

		// const websiteApiBase = "https://trackiwi-website.ddev.site" // local
		// const shareBaseUrl = "https://localhost:5277" // local

		// const websiteApiBase = "https://website.dev.trackiwi.com" // dev
		// const shareBaseUrl = "https://share.dev.trackiwi.com" // dev

		const websiteApiBase = "https://www.trackiwi.com" // production
		const shareBaseUrl = "https://share.trackiwi.com" // production

		const apiBase = ref(null)
		const token = ref(null)
		const pushToken = ref(null)
		const pushEnabled = ref(null)
		const user = ref(null)
		const userDataRefreshedAt = ref(null)
		const isBetaTester = ref(false)
		const problemOccured = ref(false)
		const connectionProblemOccured = ref(false)

		const knownEarlyAccessFeatures = [
			/* sharesng is public since 1.5.4
			{
				id: "sharesng",
				name: "Freigabe v2",
				title: "Erweiterte Freigabefunktion",
				icon: "/assets/custom-icons/100-obscured-route.svg",
			},
			*/
			/* feedback is public since 1.5.9
			{
				id: "feedback",
				name: "Feedback",
				title: "Bewertungsformular",
				icon: "/assets/custom-icons/109-star.svg",
			},
			*/
			{
				id: "low-ext-voltage-alarm",
				name: "Batteriewächter",
				title: "Alarm",
				icon: "/assets/custom-icons/077-battery-5.svg",
			},
		]

		const reset = () => {
			apiBase.value = null
			token.value = null
			pushToken.value = null
			pushEnabled.value = null
			user.value = null
			userDataRefreshedAt.value = null
			isBetaTester.value = false
			problemOccured.value = false
			connectionProblemOccured.value = false
		}

		watch(token, async () => {
			const tokenValid = await checkLoginToken()
			updatePushStatus()
			console.log("🔑 Login-Token changed. It is " + (tokenValid ? "valid ✅" : "invalid ⛔️"))
			// the push token belongs to the session of the login token
			if (tokenValid && pushToken.value) sendPushToken()
		})

		const getWebsiteApiBase = () => websiteApiBase
		const getShareBaseUrl = () => shareBaseUrl

		const login = async (loginData) => {
			try {
				console.log(`🔒 logging in at ${websiteApiBase}/api/login`)
				const deviceInfo = await Device.getInfo()

				const response = await axios.post(`${websiteApiBase}/api/login`, loginData, {
					headers: {
						Accept: "application/json",
						"App-Name": "trackiwi",
						"App-Version": appVersion.value,
						"User-Platform": Capacitor.getPlatform(),
						"User-Device": deviceInfo.model,
						"User-OS": deviceInfo.operatingSystem + " " + deviceInfo.osVersion,
						"User-Timezone": new Date().getTimezoneOffset(),
					},
				})

				if (response.status === 200 && response.data.token && response.data.user) {
					problemOccured.value = false

					user.value = response.data.user
					userDataRefreshedAt.value = new Date()
					isBetaTester.value = response.data.user.is_tester === 1
					apiBase.value = response.data.server
					token.value = response.data.token

					return true
				}

				throw {
					status: response.status,
					message: "unknown login error",
				}
			} catch (e) {
				throw {
					status: e.response?.status ?? null,
					message: e.response?.data?.message ?? e.message ?? "login failed",
					original: e,
				}
			}
		}

		const isLoggedIn = computed(() => {
			return token.value !== null
		})

		const checkLoginToken = async () => {
			try {
				const response = await dispatchApiCall("get", `/api/v2/session`)
				if (response.status !== 200 || !response.data.token || !response.data.user) {
					return false
				}
				if (!user.value) {
					user.value = response.data.user
				}
				refreshUser(true)
				return true
			} catch (e) {
				return false
			}
			return false
		}

		const refreshUser = async (force = false) => {
			// skip, if user id is unknown
			if (user.value === null || user.value.id === undefined || user.value.id === null) {
				return false
			}
			// skip, if last check was less than 10 minutes ago
			if (new Date(userDataRefreshedAt.value) > new Date(new Date() - 600000) && !force) {
				return false
			}
			try {
				const response = await dispatchWebsiteApiCall("post", `/api/login/refresh`, { user_id: user.value.id }, true)
				if (response.status !== 200 || !response.data.user) {
					return false
				}
				user.value = response.data.user
				userDataRefreshedAt.value = new Date()
				console.log("🙋‍♂️ user data refreshed from website")

				return true
			} catch (e) {
				return false
			}
			return false
		}

		const hasEarlyAccessFeature = (featureName) => {
			if (user.value === null || user.value.early_access_features === undefined || !Array.isArray(user.value.early_access_features)) {
				return false
			}
			return user.value.early_access_features.includes(featureName)
		}

		const earlyAccessFeatures = computed(() => {
			return knownEarlyAccessFeatures.filter((feature) => hasEarlyAccessFeature(feature.id))
		})

		const logout = async () => {
			try {
				await dispatchApiCall("delete", `/api/v2/session`, null, {}, 2000)
			} catch (e) {
				console.error("logout failed:", e.message ?? e)
			}

			await useRootStore().resetAll()

			if (window.location.pathname !== "/login") {
				router.replace("/login")
			}
		}

		// true, if the last push token could not be sent to the server, it is sent again when the app becomes active
		let pushTokenSendFailed = false

		/**
		 * Sends the push token to the server. The token is sent on every registration (app start), even if it did not
		 * change: the server may have lost it (e.g. a failed request or a new session).
		 */
		const registerPushToken = async (newPushToken) => {
			pushToken.value = newPushToken
			await sendPushToken()
		}

		const sendPushToken = async () => {
			if (!pushToken.value || !isLoggedIn.value) return

			try {
				await dispatchApiCall("put", `/api/v2/session/push_token`, {
					token: pushToken.value,
				})
				pushTokenSendFailed = false
				console.log("📬 Push-Token sent to the server.")
			} catch (e) {
				pushTokenSendFailed = true
				console.error("📬 Push-Token could not be sent to the server, retrying when the app becomes active", e)
			}
			updatePushStatus()
		}

		const updatePushStatus = async () => {
			if (Capacitor.isNativePlatform()) {
				let permissionStatus = await PushNotifications.checkPermissions()
				pushEnabled.value = permissionStatus.receive == "granted"
			} else {
				pushEnabled.value = Notification.permission === "granted"
			}
		}

		App.addListener("appStateChange", ({ isActive }) => {
			if (isActive) {
				updatePushStatus()
				if (pushTokenSendFailed) sendPushToken()
			}
		})

		updatePushStatus()

		/* api call function */

		const dispatchApiCall = async (method, endpoint, data, headers = {}, timeout = 20000, overrideRequestConfiguration = {}) => {
			if (apiBase.value === null) {
				throw {
					status: null,
					message: "api base not set",
				}
			}

			try {
				const deviceInfo = await Device.getInfo()

				let mergedHeaders = {
					Authorization: "Bearer " + token.value,
					Accept: "application/json",
					"App-Name": "trackiwi",
					"App-Version": appVersion.value,
					"User-Platform": Capacitor.getPlatform(),
					"User-Device": deviceInfo.model,
					"User-OS": deviceInfo.operatingSystem + " " + deviceInfo.osVersion,
					"User-Timezone": new Date().getTimezoneOffset(),
					...headers,
				}

				const requestUrl = apiBase.value + endpoint
				console.log(`📡 API (${appVersion.value}) ${method}: ${apiBase.value}${endpoint}`)

				const response = await dispatchGenericRequest(method, requestUrl, data, mergedHeaders, timeout, overrideRequestConfiguration)

				problemOccured.value = false
				return response
			} catch (e) {
				if (e.response && [401, 403].includes(e.response.status)) {
					problemOccured.value = true
				}
				throw {
					status: e.response?.status ?? null,
					message: e.response?.data?.message ?? e.message ?? "unknown error",
					original: e,
				}
			}
		}

		const dispatchWebsiteApiCall = async (method, endpoint, data = null, withAuth = false) => {
			const deviceInfo = await Device.getInfo()

			const headers = {
				Accept: "application/json",
				"App-Name": "trackiwi",
				"App-Version": appVersion.value,
				"User-Platform": Capacitor.getPlatform(),
				"User-Device": deviceInfo.model,
				"User-OS": deviceInfo.operatingSystem + " " + deviceInfo.osVersion,
				"User-Timezone": new Date().getTimezoneOffset(),
				...(withAuth
					? {
							"Trackiwi-Backend-Authorization": `Bearer ${token.value}`,
							"Trackiwi-User-Id": `${user.value && user.value.id ? user.value.id : null}`,
						}
					: {}),
			}
			const requestUrl = websiteApiBase + endpoint
			console.log(`📡 ${withAuth ? "🔒" : ""} WEBSITE ${method}: ${websiteApiBase}${endpoint}`)

			try {
				const response = await dispatchGenericRequest(method, requestUrl, data, headers)
				return response
			} catch (e) {
				if (e.response && [401, 403].includes(e.response.status)) {
					problemOccured.value = true
				}
				throw e
			}
		}

		const dispatchGenericRequest = async (method = "get", url = "/", data = null, headers = null, timeout = 20000, overrideRequestConfiguration = {}) => {
			try {
				let params = null

				if (method.toLowerCase() == "get") {
					params = data
					data = null
				}

				const requestConfiguration = {
					method: method,
					url: url,
					params: params,
					data: data,
					headers: headers,
					timeout: timeout,
					...overrideRequestConfiguration,
				}

				const response = await axios(requestConfiguration)

				// get trackiwi app commands sent by the server
				const commandHeader = response.headers["trackiwi-app-command"]
				if (commandHeader !== undefined) {
					const commandStore = useCommandStore()
					commandStore.execute(commandHeader)
				}

				const validStatusCodes = [200, 201, 202, 203, 204, 205, 206, 207, 208, 400, 401, 402, 404, 405, 406, 407, 408, 409, 410, 411, 412, 413, 414, 415, 416, 417, 418, 421, 422, 423, 424, 425, 426, 428, 429, 431, 451]

				if (validStatusCodes.includes(response.status)) {
					connectionProblemOccured.value = false
					return response
				} else {
					throw new Error("unexpected status: " + response.status)
				}
			} catch (e) {
				if (!e.response) {
					connectionProblemOccured.value = true
				}

				// timeout OR webapp network error
				if (e.code === "ECONNABORTED" || e.code === "ERR_NETWORK") {
					connectionProblemOccured.value = true
					return
				}

				throw e
			}
		}

		return {
			reset,
			login,
			isLoggedIn,
			registerPushToken,
			pushEnabled,
			getWebsiteApiBase,
			getShareBaseUrl,
			apiBase,
			user,
			refreshUser,
			userDataRefreshedAt,
			isBetaTester,
			token,
			pushToken,
			checkLoginToken,
			problemOccured,
			connectionProblemOccured,
			logout,
			dispatchApiCall,
			dispatchWebsiteApiCall,
			dispatchGenericRequest,
			hasEarlyAccessFeature,
			earlyAccessFeatures,
		}
	},
	{
		persist: true,
	},
)
